import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, Truck, Check, Crown, Star, Gem, Sparkles, X, Coins, ExternalLink, Loader2, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/Layout';
import { EmptyState } from '../components/EmptyState';
import { LoginRequired } from '../components/LoginRequired';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModal';
import { useSolana } from '../context/WalletContext';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import axios from 'axios';
import { getLocalizedText } from '../components/MultiLanguageInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rarityColors = {
  THE_ONE_ONLY: 'border-amber-500 shadow-[0_0_15px_rgba(220,38,38,0.3)]',
  LEGEND: 'border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.3)]',
  EPIC: 'border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.3)]',
  RARE: 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.3)]'
};

const tierBadgeConfig = {
  THE_ONE_ONLY: { 
    label: 'THE ONE AND ONLY', 
    bg: 'bg-gradient-to-r from-amber-600 to-pink-600', 
    text: 'text-white',
    icon: Crown
  },
  LEGEND: { 
    label: 'LEGEND', 
    bg: 'bg-gradient-to-r from-yellow-500 to-amber-600', 
    text: 'text-black',
    icon: Star
  },
  EPIC: { 
    label: 'EPIC', 
    bg: 'bg-gradient-to-r from-purple-500 to-violet-600', 
    text: 'text-white',
    icon: Gem
  },
  RARE: { 
    label: 'RARE', 
    bg: 'bg-gradient-to-r from-blue-500 to-amber-600', 
    text: 'text-white',
    icon: Sparkles
  }
};

const statusIcons = {
  kept: Package,
  shipping_requested: Truck,
  shipped: Check
};

const statusColors = {
  kept: 'text-green-500',
  shipping_requested: 'text-blue-500',
  shipped: 'text-purple-500'
};

// Category tabs
const COLLECTION_TABS = ['collection', 'nft'];

export const Inventory = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { openLogin } = useLoginModal();
  const { token, refreshUser, isAuthenticated } = useAuth();
  const { connected, publicKey, signTransaction, wallet } = useSolana();
  
  // Current language for multi-language content
  const currentLang = i18n.language || 'en';
  
  // All hooks must be called before any conditional returns
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('collection'); // 'collection' or 'nft'
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [nftSettings, setNftSettings] = useState(null);
  const [mintingItem, setMintingItem] = useState(null);
  const [mintStep, setMintStep] = useState('confirm'); // 'confirm', 'select', 'signing', 'submitting', 'success'
  const [mintResult, setMintResult] = useState(null);
  const [showNftDetails, setShowNftDetails] = useState(false);
  const [userSymbol, setUserSymbol] = useState('');
  const [selectedMintItem, setSelectedMintItem] = useState(null); // Selected item from group to mint
  
  // Sell quantity state
  const [sellQuantity, setSellQuantity] = useState(1);
  const [maxSellQuantity, setMaxSellQuantity] = useState(1);

  useEffect(() => {
    if (isAuthenticated) {
      fetchInventory();
      fetchNftSettings();
    }
  }, [isAuthenticated]);
  
  // Stay on Inventory page when not logged in — show Connect Wallet prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <SEOHead page="inventory" />
        <PageHeader title="Inventory" />
        <div className="flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md text-center bg-white border border-[#FFC300]/30 rounded-3xl p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]"
            data-testid="inventory-connect-wallet-prompt"
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FFC300] to-[#FFC300] flex items-center justify-center mx-auto mb-6 shadow-[0_10px_30px_-6px_rgba(0,157,174,0.7)]">
              <Wallet className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-black text-white mb-3 font-['Unbounded']">
              Connect Your Wallet
            </h2>
            <p className="text-[#6E6452] mb-8 leading-relaxed">
              Sorry, you must Connect your Wallet to view your Inventory.
            </p>
            <Button
              onClick={() => openLogin()}
              className="w-full h-14 bg-[#FFFDF7] hover:bg-[#FFC300] hover:text-[#06212E] text-white font-bold text-lg rounded-xl transition-all flex items-center justify-center gap-2"
              data-testid="inventory-connect-wallet-btn"
            >
              <Wallet className="w-5 h-5" />
              Connect Wallet
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const fetchNftSettings = async () => {
    try {
      const res = await axios.get(`${API}/nft/settings`);
      setNftSettings(res.data);
      // Auto-select payment method based on settings
      if (res.data.payment_method === 'coins') {
        setMintPaymentMethod('coins');
      } else if (res.data.payment_method === 'sol') {
        setMintPaymentMethod('sol');
      }
      // If hybrid, default is already 'coins' which is fine
    } catch (err) {
      console.error('Failed to fetch NFT settings:', err);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out sold items - we don't show them anymore
      const activeItems = res.data.filter(item => item.status !== 'sold');
      setItems(activeItems);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group items by item_id and item_card_name for display
  // Separate minted NFTs from regular collection items
  const groupingResult = items.reduce((acc, item) => {
    // Create a unique key based on item_id and card_name (for variants)
    const cardName = typeof item.item_card_name === 'object' 
      ? JSON.stringify(item.item_card_name) 
      : (item.item_card_name || '');
    
    // Minted NFTs go to separate list (not grouped)
    if (item.is_minted) {
      acc.nftItems.push({
        ...item,
        groupKey: item.id, // Each NFT is unique
        count: 1,
        allItems: [item],
        mintableItems: [],
        mintedCount: 1
      });
      return acc;
    }
    
    // Non-minted items get grouped
    const groupKey = `${item.item_id}_${cardName}_${item.item_rarity}`;
    
    if (!acc.collectionGroups[groupKey]) {
      acc.collectionGroups[groupKey] = {
        ...item,
        groupKey,
        count: 1,
        allItems: [item],
        mintableItems: item.is_mintable ? [item] : [],
        mintedCount: 0
      };
    } else {
      acc.collectionGroups[groupKey].count += 1;
      acc.collectionGroups[groupKey].allItems.push(item);
      if (item.is_mintable) {
        acc.collectionGroups[groupKey].mintableItems.push(item);
        acc.collectionGroups[groupKey].is_mintable = true;
      }
    }
    return acc;
  }, { collectionGroups: {}, nftItems: [] });

  const groupedCollectionItems = Object.values(groupingResult.collectionGroups);
  const groupedNftItems = groupingResult.nftItems;

  // Count for tabs
  const collectionCount = groupedCollectionItems.reduce((sum, item) => sum + item.count, 0);
  const nftCount = groupedNftItems.length;

  const handleSell = async () => {
    if (!selectedItem) return;
    setActionLoading(true);
    
    try {
      // If selling multiple, sell them one by one
      const itemsToSell = selectedItem.allItems 
        ? selectedItem.allItems.slice(0, sellQuantity)
        : [selectedItem];
      
      let totalCoins = 0;
      for (const item of itemsToSell) {
        const res = await axios.post(
          `${API}/inventory/${item.id}/sell`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        totalCoins += res.data.coins_received;
      }
      
      toast.success(t('inventory.soldSuccess', { coins: totalCoins.toLocaleString() }));
      fetchInventory();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inventory.sellFailed'));
    } finally {
      setActionLoading(false);
      setSelectedItem(null);
      setActionType(null);
      setSellQuantity(1);
    }
  };

  const handleRequestShipping = async () => {
    if (!selectedItem) return;
    setActionLoading(true);
    
    try {
      await axios.post(
        `${API}/inventory/${selectedItem.id}/request-shipping`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('inventory.shippingSuccess'));
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inventory.shippingFailed'));
    } finally {
      setActionLoading(false);
      setSelectedItem(null);
      setActionType(null);
    }
  };

  const openDetailDialog = (item) => {
    setDetailItem(item);
    setShowDetailDialog(true);
    setShowNftDetails(false); // Reset NFT details view
  };

  const closeDetailDialog = () => {
    setShowDetailDialog(false);
    setDetailItem(null);
    setShowNftDetails(false);
  };

  const handleSellFromDetail = () => {
    setSelectedItem(detailItem);
    setSellQuantity(1);
    setMaxSellQuantity(detailItem?.count || 1);
    setActionType('sell');
    closeDetailDialog();
  };

  const handleShipFromDetail = () => {
    setSelectedItem(detailItem);
    setActionType('shipping');
    closeDetailDialog();
  };

  const handleMint = async () => {
    // Use selectedMintItem if available, otherwise use mintingItem directly
    const itemToMint = selectedMintItem || mintingItem;
    if (!itemToMint) return;
    
    // Check wallet connection
    if (!connected || !publicKey || !wallet || !signTransaction) {
      toast.error(t('errors.walletNotConnected'));
      return;
    }

    // Validate user symbol (4 characters required)
    if (userSymbol.length !== 4) {
      toast.error(t('inventory.symbolRequired', { defaultValue: 'Please enter exactly 4 characters for your NFT symbol' }));
      return;
    }
    
    // Combine admin prefix + user symbol
    const fullSymbol = (nftSettings?.collection_symbol || 'TC') + userSymbol.toUpperCase();
    
    setActionLoading(true);
    setMintStep('signing');
    
    try {
      // Step 1: Prepare NFT (upload to IPFS via backend)
      const prepareRes = await axios.post(
        `${API}/nft/mint-onchain/prepare`,
        { inventory_item_id: itemToMint.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { 
        nft_id, 
        metadata_uri,
        image_uri,
        image_cid,
        metadata_cid,
        item_id,
        inventory_item_id
      } = prepareRes.data;
      
      // Step 2: Build NFT mint transaction on backend
      setMintStep('submitting');
      
      // Combine admin prefix + user symbol for full symbol
      const fullSymbol = (nftSettings?.collection_symbol || 'TC') + userSymbol.toUpperCase();
      
      const buildRes = await axios.post(
        `${API}/nft/mint-onchain/build-transaction`,
        { 
          inventory_item_id: inventory_item_id,
          metadata_uri: metadata_uri,
          name: getLocalizedText(itemToMint.item_card_name, currentLang) || getLocalizedText(itemToMint.item_name, currentLang) || 'TCG NFT',
          symbol: fullSymbol,
          seller_fee_basis_points: 500
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { transaction: txBase64, mint_address: mintAddress } = buildRes.data;
      
      // Step 3: Deserialize and sign transaction
      const { Transaction } = await import('@solana/web3.js');
      const txBuffer = Buffer.from(txBase64, 'base64');
      const transaction = Transaction.from(txBuffer);
      
      // Sign with user's wallet
      const signedTx = await signTransaction(transaction);
      const signedTxBase64 = Buffer.from(signedTx.serialize()).toString('base64');
      
      // Step 4: Submit signed transaction to backend
      const submitRes = await axios.post(
        `${API}/nft/mint-onchain/submit`,
        {
          inventory_item_id: inventory_item_id,
          signed_transaction: signedTxBase64,
          nft_id: nft_id,
          image_uri: image_uri,
          metadata_uri: metadata_uri,
          image_cid: image_cid,
          metadata_cid: metadata_cid,
          item_id: item_id,
          mint_address: mintAddress,
          tx_signature: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMintStep('success');
      setMintResult({
        ...submitRes.data,
        mint_address: mintAddress
      });
      toast.success(t('inventory.nftMinted', { defaultValue: 'NFT minted to your wallet!' }));
      fetchInventory();
      refreshUser();
      
    } catch (err) {
      console.error('Mint error:', err);
      if (err.message?.includes('User rejected') || err.message?.includes('rejected')) {
        toast.error(t('common.transactionCancelled', { defaultValue: 'Transaction cancelled' }));
      } else {
        toast.error(err.response?.data?.detail || err.message || t('inventory.mintFailed', { defaultValue: 'Failed to mint NFT' }));
      }
      setMintStep('confirm');
    } finally {
      setActionLoading(false);
    }
  };

  const closeMintDialog = () => {
    setMintingItem(null);
    setMintStep('confirm');
    setMintResult(null);
    setUserSymbol('');
    setSelectedMintItem(null);
  };

  const handleMintFromDetail = () => {
    // Check if this is a grouped item with multiple mintable NFTs
    if (detailItem.mintableItems && detailItem.mintableItems.length > 1) {
      setMintingItem(detailItem);
      setMintStep('select'); // Go to selection step first
      setSelectedMintItem(null);
    } else if (detailItem.mintableItems && detailItem.mintableItems.length === 1) {
      // Only one mintable item, select it automatically
      setMintingItem(detailItem);
      setSelectedMintItem(detailItem.mintableItems[0]);
      setMintStep('confirm');
    } else {
      // Single item (not grouped) or direct mintable
      setMintingItem(detailItem);
      setSelectedMintItem(detailItem);
      setMintStep('confirm');
    }
    setMintResult(null);
    setUserSymbol('');
    closeDetailDialog();
  };

  // Apply filters based on active tab
  const filteredItems = activeTab === 'nft' 
    ? groupedNftItems.filter(item => {
        // Tier filter
        if (tierFilter !== 'all' && item.item_rarity !== tierFilter) return false;
        return true;
      })
    : groupedCollectionItems.filter(item => {
        // Status filter
        if (statusFilter !== 'all' && item.status !== statusFilter) return false;
        // Tier filter
        if (tierFilter !== 'all' && item.item_rarity !== tierFilter) return false;
        return true;
      });

  const statusFilters = [
    { key: 'all', label: t('common.all') },
    { key: 'kept', label: t('inventory.keep') },
    { key: 'shipping_requested', label: t('inventory.shipping') },
    { key: 'shipped', label: t('inventory.shipped') },
  ];

  const tierFilters = [
    { key: 'all', label: t('inventory.allTiers', { defaultValue: 'All Tier' }) },
    { key: 'THE_ONE_ONLY', label: t('rarity.THE_ONE_ONLY'), color: 'bg-amber-600' },
    { key: 'LEGEND', label: t('rarity.LEGEND'), color: 'bg-yellow-600' },
    { key: 'EPIC', label: t('rarity.EPIC'), color: 'bg-purple-600' },
    { key: 'RARE', label: t('rarity.RARE'), color: 'bg-blue-600' },
  ];

  // Count items per tier for badge - based on active tab
  const currentItems = activeTab === 'nft' ? groupedNftItems : groupedCollectionItems;
  const tierCounts = {
    THE_ONE_ONLY: currentItems.filter(i => i.item_rarity === 'THE_ONE_ONLY').length,
    LEGEND: currentItems.filter(i => i.item_rarity === 'LEGEND').length,
    EPIC: currentItems.filter(i => i.item_rarity === 'EPIC').length,
    RARE: currentItems.filter(i => i.item_rarity === 'RARE').length,
  };

  return (
    <div className="h-screen flex flex-col bg-[#FFFDF7]">
      <SEOHead page="collection" />
      <PageHeader title={t('inventory.title')} showBack={true} />

      {/* Category Tabs - Collection / NFT */}
      <div className="px-5 py-3 flex-shrink-0">
        <div className="flex bg-zinc-900 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('collection')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'collection'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-collection"
          >
            <Package className="w-4 h-4" />
            {t('nav.collection', { defaultValue: 'Collection' })}
            {collectionCount > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'collection' ? 'bg-white/20' : 'bg-zinc-800'
              }`}>
                {collectionCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('nft')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'nft'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid="tab-nft"
          >
            <Gem className="w-4 h-4" />
            NFT
            {nftCount > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'nft' ? 'bg-white/20' : 'bg-zinc-800'
              }`}>
                {nftCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 py-2 flex-shrink-0 space-y-3">
        {/* Status Filters - Only show for collection tab */}
        {activeTab === 'collection' && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {statusFilters.map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  statusFilter === f.key
                    ? 'bg-amber-600 text-white'
                    : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                }`}
                data-testid={`filter-status-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Tier Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {tierFilters.map(f => (
            <button
              key={f.key}
              onClick={() => setTierFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                tierFilter === f.key
                  ? f.color ? `${f.color} text-white` : 'bg-zinc-600 text-white'
                  : 'bg-zinc-900/50 text-zinc-500 hover:bg-zinc-800 border border-zinc-800'
              }`}
              data-testid={`filter-tier-${f.key}`}
            >
              {f.label}
              {f.key !== 'all' && tierCounts[f.key] > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  tierFilter === f.key ? 'bg-white/20' : 'bg-zinc-700'
                }`}>
                  {tierCounts[f.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Items Grid - Scrollable */}
      <div className="flex-1 overflow-y-auto px-5 pb-24">
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 2xl:grid-cols-10 gap-2.5">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <div key={i} className="aspect-[2/3] bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            title={activeTab === 'nft' 
              ? t('inventory.noNftItems', { defaultValue: 'No NFT items yet' })
              : tierFilter !== 'all' 
                ? t('inventory.noTierItems', { tier: t(`rarity.${tierFilter}`), defaultValue: `No ${t(`rarity.${tierFilter}`)} items` })
                : t('inventory.empty')
            }
            subtitle={activeTab === 'nft' 
              ? t('inventory.mintToSeeNft', { defaultValue: 'Mint your cards to see them here!' })
              : t('inventory.emptyDescription')
            }
            size={150}
          />
        ) : (
          <>
            {/* Gold Shine Effect Styles for LEGEND cards */}
            <style>{`
              @keyframes legendShine {
                0% { background-position: -200% 0; }
                100% { background-position: 200% 0; }
              }
              .legend-shine::before {
                content: "";
                position: absolute;
                top: 0; left: 0; right: 0; bottom: 0;
                background: linear-gradient(
                  90deg,
                  transparent 0%,
                  rgba(255, 215, 0, 0.1) 25%,
                  rgba(255, 215, 0, 0.3) 50%,
                  rgba(255, 215, 0, 0.1) 75%,
                  transparent 100%
                );
                background-size: 200% 100%;
                animation: legendShine 3s ease-in-out infinite;
                pointer-events: none;
                z-index: 5;
                border-radius: inherit;
              }
              .legend-shine::after {
                content: "";
                position: absolute;
                top: 0; left: 0; right: 0; bottom: 0;
                background: radial-gradient(
                  ellipse at 30% 0%,
                  rgba(255, 215, 0, 0.15) 0%,
                  transparent 50%
                );
                pointer-events: none;
                z-index: 4;
                border-radius: inherit;
              }
            `}</style>
            
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 2xl:grid-cols-10 gap-2.5 pb-24">
              {filteredItems.map((item, index) => {
                const StatusIcon = statusIcons[item.status];
                const tierConfig = tierBadgeConfig[item.item_rarity];
                const TierIcon = tierConfig?.icon || Sparkles;
                const isLegend = item.item_rarity === 'LEGEND';
                const hasMultiple = item.count > 1;
                const isNftTab = activeTab === 'nft';
                
                return (
                  <motion.div
                    key={item.groupKey || item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => openDetailDialog(item)}
                    className={`relative aspect-[2/3] rounded-xl overflow-hidden border-2 cursor-pointer active:scale-[0.98] transition-transform ${
                      isNftTab || item.is_minted 
                        ? 'border-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                        : rarityColors[item.item_rarity]
                    } ${isLegend ? 'legend-shine' : ''}`}
                    data-testid={`inventory-item-${item.id}`}
                  >
                    <img 
                      src={item.item_image}
                      alt={getLocalizedText(item.item_card_name, currentLang) || getLocalizedText(item.item_name, currentLang)}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                    {/* Count Badge - Bottom Right (if multiple) - Only for collection tab */}
                    {!isNftTab && hasMultiple && (
                      <div className="absolute bottom-12 right-2 z-10">
                        <div className="px-2 py-1 rounded-lg bg-gradient-to-r from-amber-500 to-blue-600 text-white shadow-lg shadow-amber-500/30">
                          <span className="text-sm font-bold">x{item.count}</span>
                        </div>
                      </div>
                    )}

                    {/* NFT Tab - Show "MINTED" badge */}
                    {isNftTab && (
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center gap-1 shadow-lg shadow-purple-500/40 z-10 animate-pulse">
                        <Gem className="w-3 h-3" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">MINTED</span>
                      </div>
                    )}

                    {/* Collection Tab - NFT Badge */}
                    {!isNftTab && (item.mintableItems?.length > 0 || item.is_mintable) && (
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-gradient-to-r from-purple-900/80 to-violet-900/80 border border-purple-500/50 text-purple-300 flex items-center gap-1 shadow-lg z-10">
                        <Gem className="w-3 h-3" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          NFT{item.mintableItems?.length > 1 ? ` x${item.mintableItems.length}` : ''}
                        </span>
                      </div>
                    )}
                    
                    {/* Collection Tab - Status badge (only if not mintable) */}
                    {!isNftTab && !item.mintableItems?.length && !item.is_mintable && item.status !== 'kept' && StatusIcon && (
                      <div className={`absolute top-2 left-2 p-1.5 rounded-full bg-black/60 ${statusColors[item.status]} z-10`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                    )}

                    {/* Tier Badge - Top Right */}
                    {tierConfig && (
                      <div className={`absolute top-2 right-2 px-2 py-1 rounded-md ${tierConfig.bg} ${tierConfig.text} flex items-center gap-1 shadow-lg z-10`}>
                        <TierIcon className="w-3 h-3" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">{tierConfig.label}</span>
                      </div>
                    )}

                    {/* Content - Show card name if available, else item name */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                      <p className="text-zinc-400 text-xs mb-1 truncate">{getLocalizedText(item.pool_name, currentLang)}</p>
                      <h4 className="text-white font-semibold text-sm truncate">
                        {getLocalizedText(item.item_card_name, currentLang) || getLocalizedText(item.item_name, currentLang)}
                      </h4>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Sell Dialog with Quantity Selector */}
      <AlertDialog open={actionType === 'sell'} onOpenChange={() => { setActionType(null); setSellQuantity(1); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('inventory.confirmSell')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* Item Preview */}
                <div className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg">
                  {selectedItem?.item_image && (
                    <img 
                      src={selectedItem.item_image} 
                      alt={getLocalizedText(selectedItem.item_name, currentLang)}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-white font-semibold">{getLocalizedText(selectedItem?.item_card_name, currentLang) || getLocalizedText(selectedItem?.item_name, currentLang)}</p>
                    <p className={`text-sm font-medium ${
                      selectedItem?.item_rarity === 'THE_ONE_ONLY' ? 'text-pink-400' :
                      selectedItem?.item_rarity === 'LEGEND' ? 'text-yellow-400' :
                      selectedItem?.item_rarity === 'EPIC' ? 'text-purple-400' :
                      'text-blue-400'
                    }`}>
                      {t(`rarity.${selectedItem?.item_rarity}`, { defaultValue: selectedItem?.item_rarity?.replace('_', ' ') })}
                    </p>
                    {selectedItem?.count > 1 && (
                      <p className="text-amber-400 text-xs mt-1">
                        {t('inventory.owned', { defaultValue: 'Owned' })}: {selectedItem.count}x
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Quantity Selector - Only show if count > 1 */}
                {selectedItem?.count > 1 && (
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-xs mb-3 uppercase tracking-wider">
                      {t('inventory.selectQuantity', { defaultValue: 'Select Quantity to Sell' })}
                    </p>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setSellQuantity(Math.max(1, sellQuantity - 1))}
                        className="w-10 h-10 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white font-bold text-xl flex items-center justify-center transition-colors"
                        disabled={sellQuantity <= 1}
                      >
                        -
                      </button>
                      <div className="w-20 text-center">
                        <span className="text-white text-2xl font-bold">{sellQuantity}</span>
                        <span className="text-zinc-500 text-sm">/{selectedItem.count}</span>
                      </div>
                      <button
                        onClick={() => setSellQuantity(Math.min(selectedItem.count, sellQuantity + 1))}
                        className="w-10 h-10 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white font-bold text-xl flex items-center justify-center transition-colors"
                        disabled={sellQuantity >= selectedItem.count}
                      >
                        +
                      </button>
                    </div>
                    {/* Quick select buttons */}
                    <div className="flex justify-center gap-2 mt-3">
                      <button
                        onClick={() => setSellQuantity(1)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          sellQuantity === 1 ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                      >
                        1
                      </button>
                      {selectedItem.count >= 5 && (
                        <button
                          onClick={() => setSellQuantity(5)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            sellQuantity === 5 ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          5
                        </button>
                      )}
                      {selectedItem.count >= 10 && (
                        <button
                          onClick={() => setSellQuantity(10)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            sellQuantity === 10 ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          10
                        </button>
                      )}
                      <button
                        onClick={() => setSellQuantity(selectedItem.count)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          sellQuantity === selectedItem.count ? 'bg-amber-600 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                      >
                        {t('common.all', { defaultValue: 'All' })}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Sell Price */}
                <div className="p-4 bg-yellow-900/20 border border-yellow-900/30 rounded-lg">
                  <p className="text-zinc-400 text-xs mb-1 uppercase tracking-wider">
                    {t('inventory.totalSellPrice', { defaultValue: 'Total Sell Price' })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Coins className="w-6 h-6 text-yellow-400" />
                    <p className="text-yellow-400 text-3xl font-bold font-['Chakra_Petch']">
                      +{((selectedItem?.sell_value || 0) * sellQuantity).toLocaleString()}
                    </p>
                    <span className="text-yellow-400/70 text-lg">{t('wallet.coins')}</span>
                  </div>
                  {sellQuantity > 1 && (
                    <p className="text-zinc-500 text-xs mt-1">
                      ({selectedItem?.sell_value?.toLocaleString()} x {sellQuantity})
                    </p>
                  )}
                </div>
                
                {selectedItem?.sell_value === 0 && (
                  <p className="text-amber-400 text-xs text-center">
                    ⚠️ {t('inventory.noSellValue', { defaultValue: 'This item has no sell value (0 coins)' })}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white hover:bg-zinc-700 border-zinc-700">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSell}
              disabled={actionLoading || selectedItem?.sell_value === 0}
              className="bg-yellow-600 hover:bg-yellow-700 text-white disabled:opacity-50"
            >
              {actionLoading ? t('common.loading') : `${t('inventory.sell')} ${sellQuantity > 1 ? `(${sellQuantity}x)` : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shipping Dialog - Currently Unavailable */}
      <AlertDialog open={actionType === 'shipping'} onOpenChange={() => setActionType(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-400" />
              {t('inventory.requestShipping')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="p-4 bg-yellow-900/20 border border-yellow-900/30 rounded-lg">
                  <p className="text-yellow-400 font-medium text-sm mb-2">
                    ⚠️ {t('inventory.serviceUnavailable', { defaultValue: 'Service Unavailable' })}
                  </p>
                  <p className="text-zinc-400 text-sm">
                    {t('inventory.shippingUnavailableDesc', { defaultValue: 'Card shipping requests are currently unavailable. Please try again later or contact customer service for more information.' })}
                  </p>
                </div>
                
                <p className="text-zinc-500 text-xs text-center">
                  {t('inventory.card', { defaultValue: 'Card' })}: <span className="text-white">{getLocalizedText(selectedItem?.item_card_name, currentLang) || getLocalizedText(selectedItem?.item_name, currentLang)}</span>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-full bg-zinc-800 text-white hover:bg-zinc-700 border-zinc-700">
              {t('common.close')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mint NFT Dialog */}
      <AlertDialog open={!!mintingItem} onOpenChange={closeMintDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Gem className="w-5 h-5 text-purple-400" />
              {mintStep === 'success' ? t('inventory.nftMinted', { defaultValue: 'NFT Minted!' }) : t('inventory.mintAsNFT', { defaultValue: 'Mint as NFT' })}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* Item Preview */}
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <img
                    src={selectedMintItem?.item_image || mintingItem?.item_image}
                    alt={getLocalizedText(mintingItem?.item_name, currentLang)}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <p className="text-white font-medium">{getLocalizedText(selectedMintItem?.item_card_name || mintingItem?.item_card_name, currentLang) || getLocalizedText(mintingItem?.item_name, currentLang)}</p>
                    <p className="text-zinc-500 text-sm">{getLocalizedText(mintingItem?.pool_name, currentLang)}</p>
                    {mintingItem?.mintableItems?.length > 1 && (
                      <p className="text-purple-400 text-xs mt-1">
                        {mintingItem.mintableItems.length} NFT {t('inventory.availableToMint', { defaultValue: 'available to mint' })}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Select NFT Step - When multiple mintable items in group */}
                {mintStep === 'select' && mintingItem?.mintableItems?.length > 1 && (
                  <div className="space-y-3">
                    <p className="text-zinc-300 text-sm">
                      {t('inventory.selectNftToMint', { defaultValue: 'Select which NFT card you want to mint:' })}
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {mintingItem.mintableItems.map((item, idx) => (
                        <div
                          key={item.id}
                          onClick={() => setSelectedMintItem(item)}
                          className={`p-2 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedMintItem?.id === item.id
                              ? 'border-purple-500 bg-purple-500/20'
                              : 'border-zinc-700 bg-zinc-800/50 hover:border-purple-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <img
                              src={item.item_image}
                              alt={`NFT ${idx + 1}`}
                              className="w-10 h-14 rounded object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-medium truncate">NFT #{idx + 1}</p>
                              <p className="text-zinc-500 text-[10px]">ID: ...{item.id.slice(-6)}</p>
                              {selectedMintItem?.id === item.id && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Check className="w-3 h-3 text-purple-400" />
                                  <span className="text-purple-400 text-[10px]">{t('common.selected', { defaultValue: 'Selected' })}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      onClick={() => setMintStep('confirm')}
                      disabled={!selectedMintItem}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white disabled:opacity-50"
                    >
                      {t('common.continue', { defaultValue: 'Continue' })}
                    </Button>
                  </div>
                )}
                
                {/* Confirm Step */}
                {mintStep === 'confirm' && (
                  <>
                    <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <p className="text-purple-300 text-sm">
                        {t('inventory.mintDescription', { defaultValue: 'This will mint your item as an NFT on Solana blockchain. The NFT will be sent to your connected wallet and can be viewed on Solscan.' })}
                      </p>
                    </div>

                    {/* NFT Symbol Input */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2">
                      <label className="text-zinc-300 text-sm font-medium block">
                        {t('inventory.createSymbol', { defaultValue: 'Create Your NFT Symbol' })}
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-2 bg-purple-600 rounded-l-lg text-white font-mono font-bold text-sm">
                          {nftSettings?.collection_symbol || 'TC'}
                        </span>
                        <input
                          type="text"
                          value={userSymbol}
                          onChange={(e) => setUserSymbol(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))}
                          placeholder={t('inventory.enterSymbol', { defaultValue: 'ABCD' })}
                          maxLength={4}
                          className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-r-lg text-white font-mono uppercase placeholder:text-zinc-600 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                      <p className="text-zinc-500 text-xs">
                        {t('inventory.symbolHint', { defaultValue: 'Enter 4 characters. Final symbol:' })} <span className="text-purple-400 font-mono font-bold">{(nftSettings?.collection_symbol || 'TC') + (userSymbol || '????')}</span>
                      </p>
                    </div>

                    {/* Wallet Check */}
                    {!connected ? (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-amber-400 text-sm">
                          {t('inventory.connectToMint', { defaultValue: 'Please connect your wallet first to mint NFTs.' })}
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <p className="text-green-400 text-sm">
                          {t('auth.walletConnected')}: {publicKey?.toBase58()?.slice(0, 6)}...{publicKey?.toBase58()?.slice(-4)}
                        </p>
                      </div>
                    )}

                    {/* Cost Display */}
                    <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-sm mb-1">{t('inventory.networkFeesOnly', { defaultValue: 'Network Fees Only' })}</p>
                      <p className="text-white text-lg font-bold">
                        ~0.01 SOL
                      </p>
                      <p className="text-zinc-500 text-xs mt-1">{t('inventory.solanaNetworkFees', { defaultValue: 'Solana network fees' })}</p>
                    </div>
                  </>
                )}

                {/* Signing Step */}
                {mintStep === 'signing' && (
                  <div className="py-8 text-center space-y-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-900/50 to-violet-900/50 flex items-center justify-center mx-auto relative">
                      <Wallet className="w-10 h-10 text-purple-400" />
                      <div className="absolute inset-0 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">{t('wallet.waitingWalletApproval', { defaultValue: 'Waiting for Wallet...' })}</p>
                      <p className="text-zinc-400 text-sm mt-1">{t('wallet.pleaseConfirmInWallet', { defaultValue: 'Please confirm the transaction in your wallet' })}</p>
                      <p className="text-purple-400 text-xs mt-3 animate-pulse">{t('wallet.checkYourWallet', { defaultValue: 'Check your Phantom/Solflare wallet' })}</p>
                    </div>
                  </div>
                )}

                {/* Submitting Step */}
                {mintStep === 'submitting' && (
                  <div className="py-8 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto">
                      <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">{t('inventory.submittingToSolana', { defaultValue: 'Submitting to Solana...' })}</p>
                      <p className="text-zinc-400 text-sm">{t('wallet.transactionInProgress', { defaultValue: 'Transaction in progress...' })}</p>
                    </div>
                  </div>
                )}

                {/* Success Step */}
                {mintStep === 'success' && mintResult && (
                  <div className="py-4 text-center space-y-4">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-white font-medium">{t('inventory.nftMintedSuccess', { defaultValue: 'Your NFT has been minted!' })}</p>
                    <p className="text-zinc-500 text-sm">
                      {t('inventory.nftInWallet', { defaultValue: 'The NFT is now in your wallet and can be viewed on Solscan.' })}
                    </p>
                    
                    <div className="flex flex-col gap-2">
                      <a
                        href={mintResult.explorer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View on Solscan
                      </a>
                      <a
                        href={mintResult.metadata_uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 text-sm transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Metadata
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Footer - Only show on confirm step */}
          {mintStep === 'confirm' && (
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 text-white hover:bg-zinc-700 border-zinc-700">
                {t('common.cancel')}
              </AlertDialogCancel>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  handleMint();
                }}
                disabled={actionLoading || !connected || userSymbol.length !== 4}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white disabled:opacity-50"
              >
                {t('inventory.mintAsNFT', { defaultValue: 'Mint NFT' })}
              </Button>
            </AlertDialogFooter>
          )}
          
          {/* Success Footer */}
          {mintStep === 'success' && (
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={closeMintDialog}
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                {t('common.done')}
              </AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Card Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={closeDetailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xs p-0 overflow-hidden max-h-[85vh]">
          {detailItem && (() => {
            const tierConfig = tierBadgeConfig[detailItem.item_rarity];
            const TierIcon = tierConfig?.icon || Sparkles;
            
            return (
              <div className="relative overflow-y-auto max-h-[85vh]">
                {/* Close button */}
                <button
                  onClick={closeDetailDialog}
                  className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Card Image - Full display with white background */}
                <div className="bg-white flex items-center justify-center p-2 min-h-[200px] relative">
                  <img
                    src={detailItem.item_image}
                    alt={getLocalizedText(detailItem.item_name, currentLang)}
                    className="max-w-full max-h-[300px] object-contain"
                  />
                  
                  {/* Tier Badge on image */}
                  {tierConfig && (
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-md ${tierConfig.bg} ${tierConfig.text} flex items-center gap-1 shadow-lg`}>
                      <TierIcon className="w-3 h-3" />
                      <span className="text-xs font-bold uppercase tracking-wider">{tierConfig.label}</span>
                    </div>
                  )}
                </div>

                {/* Card Details - More compact */}
                <div className="p-3 space-y-3">
                  {/* Name & Pool */}
                  <div>
                    <h3 className="text-white text-base font-bold font-['Chakra_Petch'] leading-tight">
                      {getLocalizedText(detailItem.item_card_name, currentLang) || getLocalizedText(detailItem.item_name, currentLang)}
                    </h3>
                    <p className="text-zinc-400 text-xs">{getLocalizedText(detailItem.pool_name, currentLang)}</p>
                    {/* Show original item name if card variant */}
                    {detailItem.item_card_name && getLocalizedText(detailItem.item_card_name, currentLang) !== getLocalizedText(detailItem.item_name, currentLang) && (
                      <p className="text-zinc-500 text-[10px] mt-0.5">from: {getLocalizedText(detailItem.item_name, currentLang)}</p>
                    )}
                  </div>

                  {/* Stats Grid - Compact */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Tier */}
                    <div className="p-2 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{t('inventory.rarity')}</p>
                      <p className={`font-bold text-sm ${tierConfig?.text || 'text-white'}`}>
                        {t(`rarity.${detailItem.item_rarity}`, { defaultValue: tierConfig?.label || detailItem.item_rarity })}
                      </p>
                    </div>
                    
                    {/* Sell Value */}
                    <div className="p-2 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{t('inventory.sellPrice')}</p>
                      <p className="text-yellow-400 font-bold text-sm flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        {detailItem.sell_value || 0}
                      </p>
                    </div>
                  </div>

                  {/* Status - Compact */}
                  <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                    <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{t('common.status')}</span>
                    <div className="flex items-center gap-2">
                      {detailItem.is_minted && (
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] font-bold rounded">
                          NFT
                        </span>
                      )}
                      <span className={`font-medium text-sm ${
                        detailItem.status === 'kept' ? 'text-green-400' :
                        detailItem.status === 'shipping_requested' ? 'text-blue-400' :
                        detailItem.status === 'shipped' ? 'text-purple-400' : 'text-zinc-400'
                      }`}>
                        {detailItem.status === 'kept' ? t('inventory.keep') :
                         detailItem.status === 'shipping_requested' ? t('inventory.shipping') :
                         detailItem.status === 'shipped' ? t('inventory.shipped') : detailItem.status}
                      </span>
                    </div>
                  </div>

                  {/* Obtained Date - Smaller */}
                  <p className="text-zinc-600 text-[10px] text-center">
                    {t('inventory.obtainedDate')} {new Date(detailItem.obtained_at).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>

                  {/* Action Buttons - Compact */}
                  {detailItem.status === 'kept' && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSellFromDetail}
                          className="flex-1 h-10 bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-sm"
                          data-testid="detail-sell-btn"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          {t('inventory.sell')}
                        </Button>
                        <Button
                          onClick={handleShipFromDetail}
                          variant="outline"
                          className="flex-1 h-10 border-zinc-700 text-white hover:bg-zinc-800 font-bold text-sm"
                          data-testid="detail-ship-btn"
                        >
                          <Truck className="w-4 h-4 mr-1" />
                          {t('inventory.requestShipping', { defaultValue: 'Ship' })}
                        </Button>
                      </div>
                      
                      {/* Mint NFT Button - Only show if item is mintable and not minted */}
                      {nftSettings?.is_active && !detailItem.is_minted && detailItem.is_mintable && (
                        <Button
                          onClick={handleMintFromDetail}
                          className="w-full h-10 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-sm"
                          data-testid="detail-mint-btn"
                        >
                          <Gem className="w-4 h-4 mr-1" />
                          {t('inventory.mintAsNFT', { defaultValue: 'Mint as NFT' })}
                        </Button>
                      )}
                      
                      {/* Detail NFT Button - Show if item is minted */}
                      {detailItem.is_minted && !showNftDetails && (
                        <Button
                          onClick={() => setShowNftDetails(true)}
                          className="w-full h-10 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-sm"
                          data-testid="detail-nft-btn"
                        >
                          <Gem className="w-4 h-4 mr-1" />
                          {t('inventory.nftDetails', { defaultValue: 'NFT Details' })}
                        </Button>
                      )}
                      
                      {/* NFT On-Chain Details - Show when toggle is on */}
                      {detailItem.is_minted && showNftDetails && (
                        <div className="p-3 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg space-y-3">
                          {/* NFT Header with close button */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                                <Gem className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="text-purple-400 text-sm font-bold">On-Chain NFT</p>
                                <p className="text-zinc-500 text-[10px]">Solana Blockchain</p>
                              </div>
                            </div>
                            <button
                              onClick={() => setShowNftDetails(false)}
                              className="w-6 h-6 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          
                          {/* NFT Mint Address */}
                          {detailItem.nft_mint_address && (
                            <div className="p-2 bg-black/30 rounded-lg">
                              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Mint Address</p>
                              <p className="text-purple-300 text-xs font-mono break-all">
                                {detailItem.nft_mint_address}
                              </p>
                            </div>
                          )}
                          
                          {/* NFT Links */}
                          <div className="flex flex-col gap-2">
                            {detailItem.nft_mint_address && (
                              <a
                                href={`https://solscan.io/token/${detailItem.nft_mint_address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-xs font-medium transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                View on Solscan
                              </a>
                            )}
                            {detailItem.nft_metadata_uri && (
                              <a 
                                href={detailItem.nft_metadata_uri.replace('ipfs://', 'https://gateway.pinata.cloud/ipfs/')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 text-xs font-medium transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                View Metadata (IPFS)
                              </a>
                            )}
                          </div>
                          
                          {/* Transaction Signature */}
                          {detailItem.nft_tx_signature && (
                            <div className="pt-2 border-t border-purple-500/20">
                              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Transaction</p>
                              <a
                                href={`https://solscan.io/tx/${detailItem.nft_tx_signature}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 text-[10px] font-mono hover:underline break-all"
                              >
                                {detailItem.nft_tx_signature.slice(0, 20)}...{detailItem.nft_tx_signature.slice(-20)}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
